import { Analytics } from '@cobliteam/analytics';
import { ConditionCategory } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/conditions';
import { RuleCategory } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/rules';
import { Location, type UUID } from '@cobliteam/common-types';
import { AlertHookType } from '@cobliteam/hooks/src/useAlertHooks/types';
import useTriggerAlertHook from '@cobliteam/hooks/src/useAlertHooks/useTriggerAlertHook';
import { useTranslation } from '@cobliteam/i18n';
import { usePermissions } from '@cobliteam/permissions';
import { Button } from '@cobliteam/ui/components';
import Icon from '@cobliteam/ui/icons';
import queryString from 'query-string';
import type React from 'react';

import type { ProcessedStopRouteStep } from '../../../../store';
import { buildTrackingMapFallbackEventId } from '../../../../utils/trackingEventDeepLink';
import { StopPopup } from '../../../StopPopup/StopPopup';

export interface HistoryStopPopupProps {
  stop: ProcessedStopRouteStep;
  vehicleId: UUID;
  idleTimeHighlighted: boolean;
  filteredDuration?: number;
}

export const HistoryStopPopup: React.FC<HistoryStopPopupProps> = ({ stop, vehicleId, idleTimeHighlighted, filteredDuration }) => {
  const { t } = useTranslation('tracking');
  const { triggerAlertHook } = useTriggerAlertHook();
  const { isAccessAllowed } = usePermissions();
  const canCreateAlertTrigger = isAccessAllowed('config-and-view:custom-alerts');

  const handleCheckMap = () => {
    Analytics.track('Opened Vehicle Tracking On Map From Tracking History Stop Popup');

    const params = queryString.stringify({
      vehicle_id: vehicleId,
      date: stop.startTime,
      eventId: buildTrackingMapFallbackEventId('stop', stop.index),
      event_type: 'stop',
      event_step: stop.index + 1,
    });
    const url = `${window.location.origin}/#${Location.TRACKING}?${params}`;
    window.open(url, '_blank')!.focus();
  };

  const handleCreateIdleEngineRule = async () => {
    Analytics.track('Created Idle Engine Rule by Hook Popup');
    const opened = await triggerAlertHook(AlertHookType.IDLE_ENGINE, { vehicleIds: [vehicleId] }, { force: true });
    if (opened) return;

    const params = queryString.stringify({
      templateRule: RuleCategory.IDLE_ENGINE,
      templateCondition: ConditionCategory.IDLE_ENGINE_10_MINUTES,
      vehicleId,
    });
    window.open(`${window.location.origin}/#${Location.ALERTS_TRIGGERS_CREATE}?${params}`, '_blank')?.focus();
  };

  return (
    <StopPopup stop={stop} filteredDuration={filteredDuration} idleTimeHighlighted={idleTimeHighlighted}>
      {canCreateAlertTrigger && (
        <Button variant='outlined' size='sm' className='w-full flex-1' startIcon={<Icon name='plus' />} onClick={handleCreateIdleEngineRule}>
          {t('create_idle_engine_rule')}
        </Button>
      )}
      <Button variant='ghost' className='w-full flex-1' size='sm' startIcon={<Icon name='external-link' />} onClick={handleCheckMap}>
        {t('check_map')}
      </Button>
    </StopPopup>
  );
};
